import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Users, ShoppingBag, Package, IndianRupee, ArrowRight, Image as ImageIcon, Settings, LayoutGrid, BarChart3, MessageCircle, CreditCard } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { adminApi } from '../lib/api';

export default function AdminDashboard() {
  const [stats, setStats] = useState({
    total_users: 0,
    total_orders: 0,
    total_products: 0,
    total_revenue: 0,
    pending_encashments: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    setLoading(true);
    try {
      const response = await adminApi.getStats();
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    { title: 'Total Users', value: stats.total_users, icon: Users, color: 'text-primary', link: '/admin/users' },
    { title: 'Total Orders', value: stats.total_orders, icon: ShoppingBag, color: 'text-accent', link: '/admin/orders' },
    { title: 'Total Products', value: stats.total_products, icon: Package, color: 'text-secondary', link: '/admin/products' },
    { title: 'Pending Encashments', value: stats.pending_encashments, icon: CreditCard, color: 'text-pink-500', link: '/admin/encashment' }
  ];

  return (
    <div className="min-h-screen pt-32 px-6 md:px-12 pb-20" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="text-4xl md:text-5xl font-semibold tracking-tight mb-4" data-testid="dashboard-title">Admin Dashboard</h1>
          <p className="text-base md:text-lg text-muted-foreground mb-12">Manage Ved3dCreations store</p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {statCards.map((stat, index) => (
              <motion.div
                key={stat.title}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                data-testid={`stat-card-${stat.title.toLowerCase().replace(' ', '-')}`}
              >
                <Link to={stat.link}>
                  <div className="glass-morphic rounded-2xl p-6 hover:border-primary/30 transition-all duration-300 group">
                    <div className="flex items-center justify-between mb-4">
                      <stat.icon className={`h-8 w-8 ${stat.color}`} />
                      <ArrowRight className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors duration-300" />
                    </div>
                    {loading ? (
                      <Skeleton className="h-9 w-24 mb-1" />
                    ) : (
                      <p className="text-3xl font-bold mb-1" data-testid={`stat-value-${stat.title.toLowerCase().replace(' ', '-')}`}>{stat.value}</p>
                    )}
                    <p className="text-sm text-muted-foreground">{stat.title}</p>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Link to="/admin/products" data-testid="manage-products-link">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5 }}
                className="glass-morphic rounded-2xl p-8 hover:border-primary/30 transition-all duration-300 group h-full"
              >
                <Package className="h-12 w-12 text-primary mb-4" />
                <h3 className="text-2xl font-semibold mb-2 group-hover:text-primary transition-colors duration-300">Manage Products</h3>
                <p className="text-muted-foreground">Add, edit, or remove products from your store</p>
              </motion.div>
            </Link>

            <Link to="/admin/categories" data-testid="manage-categories-link">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.05 }}
                className="glass-morphic rounded-2xl p-8 hover:border-primary/30 transition-all duration-300 group h-full"
              >
                <LayoutGrid className="h-12 w-12 text-orange-500 mb-4" />
                <h3 className="text-2xl font-semibold mb-2 group-hover:text-primary transition-colors duration-300">Manage Categories</h3>
                <p className="text-muted-foreground">Add, edit, or remove product categories</p>
              </motion.div>
            </Link>

            <Link to="/admin/orders" data-testid="view-orders-link">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="glass-morphic rounded-2xl p-8 hover:border-primary/30 transition-all duration-300 group h-full"
              >
                <ShoppingBag className="h-12 w-12 text-accent mb-4" />
                <h3 className="text-2xl font-semibold mb-2 group-hover:text-primary transition-colors duration-300">View Orders</h3>
                <p className="text-muted-foreground">Track and manage customer orders</p>
              </motion.div>
            </Link>

            <Link to="/admin/analytics" data-testid="view-analytics-link">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.12 }}
                className="glass-morphic rounded-2xl p-8 hover:border-primary/30 transition-all duration-300 group h-full"
              >
                <BarChart3 className="h-12 w-12 text-indigo-500 mb-4" />
                <h3 className="text-2xl font-semibold mb-2 group-hover:text-primary transition-colors duration-300">Data Analytics</h3>
                <p className="text-muted-foreground">View sales trends and performance metrics</p>
              </motion.div>
            </Link>

            <Link to="/admin/chat" data-testid="view-chat-link">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.14 }}
                className="glass-morphic rounded-2xl p-8 hover:border-primary/30 transition-all duration-300 group h-full"
              >
                <MessageCircle className="h-12 w-12 text-emerald-500 mb-4" />
                <h3 className="text-2xl font-semibold mb-2 group-hover:text-primary transition-colors duration-300">Customer Support</h3>
                <p className="text-muted-foreground">Chat with customers and provide assistance</p>
              </motion.div>
            </Link>

            <Link to="/admin/hero" data-testid="manage-hero-link">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.15 }}
                className="glass-morphic rounded-2xl p-8 hover:border-primary/30 transition-all duration-300 group h-full"
              >
                <ImageIcon className="h-12 w-12 text-yellow-500 mb-4" />
                <h3 className="text-2xl font-semibold mb-2 group-hover:text-primary transition-colors duration-300">Hero Images</h3>
                <p className="text-muted-foreground">Manage carousel images on the home page</p>
              </motion.div>
            </Link>

            <Link to="/admin/users" data-testid="manage-users-link">
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="glass-morphic rounded-2xl p-8 hover:border-primary/30 transition-all duration-300 group h-full"
              >
                <Users className="h-12 w-12 text-secondary mb-4" />
                <h3 className="text-2xl font-semibold mb-2 group-hover:text-primary transition-colors duration-300">Manage Users</h3>
                <p className="text-muted-foreground">View registered users and their details</p>
              </motion.div>
            </Link>

            <Link to="/admin/settings" data-testid="manage-settings-link">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.25 }}
                className="glass-morphic rounded-2xl p-8 hover:border-primary/30 transition-all duration-300 group h-full"
              >
                <Settings className="h-12 w-12 text-slate-400 mb-4" />
                <h3 className="text-2xl font-semibold mb-2 group-hover:text-primary transition-colors duration-300">Store Settings</h3>
                <p className="text-muted-foreground">Configure payment options and store settings</p>
              </motion.div>
            </Link>

            <Link to="/admin/encashment" data-testid="manage-encashment-link">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="glass-morphic rounded-2xl p-8 hover:border-primary/30 transition-all duration-300 group h-full"
              >
                <CreditCard className="h-12 w-12 text-pink-500 mb-4" />
                <h3 className="text-2xl font-semibold mb-2 group-hover:text-primary transition-colors duration-300">Encashment Requests</h3>
                <p className="text-muted-foreground">Review and approve credit encashment requests</p>
              </motion.div>
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
